

import React, { useEffect, useState } from 'react';
import { FaUser, FaCalendarAlt, FaClock, FaComments, FaPaperPlane, FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import "../styles/GetStarted.css";
import Footer from "../components/Footer.jsx";

function GetStarted() {
    const navigate = useNavigate();
    const [talkshows, setTalkshows] = useState([]);
    const [filteredTalkshows, setFilteredTalkshows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeChats, setActiveChats] = useState({});
    const [chatMessages, setChatMessages] = useState({});
    const [inputTexts, setInputTexts] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [fetchedComments, setFetchedComments] = useState({});
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [expandedDescription, setExpandedDescription] = useState({}); // Track expanded descriptions

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_API}/shows/`)
            .then((response) => {
                if (!response.ok) throw new Error("Failed to fetch talk shows");
                return response.json();
            })
            .then((data) => {
                // Filter only public talk shows
                const publicTalkshows = data.filter(talkshow => !talkshow.is_private);
    
                setTalkshows(publicTalkshows);
                setFilteredTalkshows(publicTalkshows);
    
                const initialChats = {};
                const initialMessages = {};
                const initialInputs = {};
    
                publicTalkshows.forEach(talkshow => {
                    initialChats[talkshow.id] = false;
                    initialMessages[talkshow.id] = [];
                    initialInputs[talkshow.id] = "";
                });
    
                setActiveChats(initialChats);
                setChatMessages(initialMessages);
                setInputTexts(initialInputs);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, []);
    
    const fetchComments = async (show) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_API}/shows/comments/${show}/`);
            if (!response.ok) throw new Error("Failed to fetch comments");
            const data = await response.json();
            setFetchedComments(prev => ({
                ...prev,
                [show]: data.map(comment => ({
                    id: comment.id,
                    userName: comment.userName,
                    content: comment.content,
                    createdAt: comment.created_at
                }))
            }));
        } catch (error) {
            console.error('Error fetching comments:', error);
        }
    };

    const toggleChat = (talkshowId) => {
        setActiveChats(prev => ({
            ...prev,
            [talkshowId]: !prev[talkshowId]
        }));

        if (!activeChats[talkshowId]) {
            fetchComments(talkshowId);
        }
    };

    const handleSearch = () => {
        const lowerCaseSearch = searchTerm.toLowerCase().trim();
        
        if (!lowerCaseSearch) {
            setFilteredTalkshows(talkshows);
            return;
        }

        if (lowerCaseSearch.startsWith("#")) {
            const filtered = talkshows.filter((talkshow) => {
                if (!talkshow.description) return false;
                
                const descriptionLower = talkshow.description.toLowerCase();
                const hashtags = descriptionLower.match(/#[a-z0-9_]+/g) || [];
                
                return hashtags.some(tag => tag === lowerCaseSearch);
            });
            setFilteredTalkshows(filtered);
        } else {
            const filtered = talkshows.filter((talkshow) => {
                if (talkshow.host?.userName?.toLowerCase().includes(lowerCaseSearch)) {
                    return true;
                }
                
                if (talkshow.co_hosts?.some(coHost => 
                    coHost.userName?.toLowerCase().includes(lowerCaseSearch)
                )) {
                    return true;
                }
                
                if (talkshow.title?.toLowerCase().includes(lowerCaseSearch)) {
                    return true;
                }
                
                return false;
            });
            setFilteredTalkshows(filtered);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (!value.trim()) {
            setFilteredTalkshows(talkshows);
        }
    };

    if (loading) return <p className="loading-message">Loading...</p>;
    if (error) return <p className="error-message">{error}</p>;

    const today = new Date().toISOString().split('T')[0];
    const upcomingTalkshows = filteredTalkshows.filter(talkshow => {
        const showDateTime = new Date(`${talkshow.show_date}T${talkshow.start_time}`);
        const now = new Date();
        return showDateTime > now;
    });

    return (
        <div className="get-started-container" style={{
            border: isDarkMode ? 'none' : '1px solid #f0f0f0'
        }}>
            <div className="talkshow-content">
                {upcomingTalkshows.length === 0 ? (
                    <p className="no-results" style={{ color: 'black' }}>No matching talkshows found</p>
                ) : (
                    upcomingTalkshows.map((talkshow) => (
                        <div key={talkshow.id} className="talkshow-card-container" style={{
                            border: isDarkMode ? 'none' : '',
                            boxShadow: isDarkMode ? '0 2px 8px rgba(255,255,255,0.1)' : '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                            <div className="talkshow-card">
                                <div>
                                    <h2 className="talkshow-title-hedding">{talkshow.title}</h2>
                                    
                                    <p className="talkshow-description">
                                    {talkshow.description.length > 900 
                                        ? `${talkshow.description.slice(0, 900)}... `
                                        : talkshow.description
                                    }

                                    {talkshow.description.length > 200 && (
                                        <span
                                        onClick={() => navigate(`/read-more/${talkshow.id}`)}
                                        className="read-more-toggle"
                                        style={{ color: 'blue', cursor: 'pointer' }}
                                        >
                                        Read More
                                        </span>
                                    )}
                                    </p>

                                    {/* Combined Hosts and Members section with Date/Time in a single row */}
                                    <div className="hosts-members-container">
                                        <div className="host-section">
                                            <h3 className="section-title">Hosted By:</h3>
                                            <ul className="hosts-list">
                                                {talkshow.co_hosts?.map((coHost) => (
                                                    <li key={coHost.id} className="host-item">
                                                        <div className="host-avatar">
                                                            {coHost.profileImgUrl ? (
                                                                <img
                                                                    src={coHost.profileImgUrl}
                                                                    alt={coHost.userName}
                                                                    className="host-image"
                                                                />
                                                            ) : (
                                                                <FaUser className="default-avatar" />
                                                            )}
                                                        </div>
                                                        <strong>{coHost.userName}</strong>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="members-section">
                                            <h3 className="section-title">Members:</h3>
                                            <ul className="members-list">
                                                {talkshow.members?.map((member) => (
                                                    <li key={`member-${member.id}`} className="member-item">
                                                        <div className="member-avatar">
                                                            {member.profileImgUrl ? (
                                                                <img 
                                                                    src={member.profileImgUrl} 
                                                                    alt="Member" 
                                                                    className="member-image" 
                                                                />
                                                            ) : (
                                                                <FaUser className="default-avatar" />
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="datetime-section">
                                            <div className="time-info-item">
                                                <FaCalendarAlt className="time-icon" size={15} />
                                                <span>
                                                    {talkshow.show_date} 
                                                    {talkshow.show_date > today && " (Upcoming)"}
                                                </span>
                                            </div>
                                            <div className="time-info-item">
                                                <FaClock className="time-icon" size={15} />
                                                <span>{talkshow.start_time}</span>
                                            </div>
                                        </div>
                                        <div className="talkshow-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => toggleChat(talkshow.id)}
                                        title={activeChats[talkshow.id] ? 'Close Chat' : 'View Comments'}
                                        style={{
                                            background: '#207534', // Green background
                                            border: 'none',
                                            borderRadius: '4px', // Slightly rounded corners
                                            width: '36px',
                                            height: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            transition: 'all 0.3s ease',
                                            padding: 0,
                                            margin: 0,
                                            marginLeft: 'auto' // Ensures right alignment
                                        }}
                                    >
                                        <FaComments 
                                            style={{
                                                fontSize: '18px',
                                                color: 'white' // White icon
                                            }} 
                                        />
                                        {fetchedComments[talkshow.id]?.length > 0 && (
                                            <span 
                                                style={{
                                                    position: 'absolute',
                                                    top: '-5px',
                                                    right: '-5px',
                                                    backgroundColor: 'white',
                                                    color: '#207534',
                                                    borderRadius: '50%',
                                                    width: '18px',
                                                    height: '18px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    border: '2px solid #207534'
                                                }}
                                            >
                                                {fetchedComments[talkshow.id]?.length}
                                            </span>
                                        )}
                                    </button>
                                </div>
                                    </div>
                                </div>

                                {/* <div className="talkshow-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => toggleChat(talkshow.id)}
                                        title={activeChats[talkshow.id] ? 'Close Chat' : 'View Comments'}
                                        style={{
                                            background: '#207534', // Green background
                                            border: 'none',
                                            borderRadius: '4px', // Slightly rounded corners
                                            width: '36px',
                                            height: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            transition: 'all 0.3s ease',
                                            padding: 0,
                                            margin: 0,
                                            marginLeft: 'auto' // Ensures right alignment
                                        }}
                                    >
                                        <FaComments 
                                            style={{
                                                fontSize: '18px',
                                                color: 'white' // White icon
                                            }} 
                                        />
                                        {fetchedComments[talkshow.id]?.length > 0 && (
                                            <span 
                                                style={{
                                                    position: 'absolute',
                                                    top: '-5px',
                                                    right: '-5px',
                                                    backgroundColor: 'white',
                                                    color: '#207534',
                                                    borderRadius: '50%',
                                                    width: '18px',
                                                    height: '18px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    border: '2px solid #207534'
                                                }}
                                            >
                                                {fetchedComments[talkshow.id]?.length}
                                            </span>
                                        )}
                                    </button>
                                </div> */}
                            </div>

                            {activeChats[talkshow.id] && (
                                <div 
                                    className="greet-chat-container" 
                                    style={{ 
                                        width: '100%',
                                        background: 'white',
                                        padding: '20px',
                                        boxSizing: 'border-box',
                                        borderTop: '1px solid #f0f0f0',
                                        borderRadius: '0 0 12px 12px',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                                        marginTop: '-1px'
                                    }}
                                >
                                    <div 
                                        className="chat-messages" 
                                        style={{
                                            height: '300px',
                                            overflowY: 'auto',
                                            padding: '15px',
                                            marginBottom: '20px',
                                            background: '#f9f9f9',
                                            borderRadius: '8px',
                                            border: '1px solid #eee'
                                        }}
                                    >
                                        {fetchedComments[talkshow.id]?.map((comment) => (
                                            <div
                                                key={`comment-${comment.id}`}
                                                className="message user"
                                                style={{
                                                    display: 'flex',
                                                    marginBottom: '12px',
                                                    justifyContent: 'flex-end',
                                                    width: '100%'
                                                }}
                                            >
                                                <div 
                                                    className="message-bubble"
                                                    style={{
                                                        maxWidth: '75%',
                                                        padding: '12px 16px',
                                                        borderRadius: '18px',
                                                        fontSize: '0.95rem',
                                                        lineHeight: '1.4',
                                                        wordBreak: 'break-word',
                                                        position: 'relative',
                                                        background: '#000000',
                                                        color: 'white',
                                                        borderBottomRightRadius: '4px'
                                                    }}
                                                >
                                                    <div 
                                                        className="comment-user"
                                                        style={{
                                                            fontWeight: '600',
                                                            fontSize: '0.8rem',
                                                            marginBottom: '4px',
                                                            display: 'block'
                                                        }}
                                                    >
                                                        {comment.userName}:
                                                    </div>
                                                    {comment.content}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default GetStarted;